import type {
  QuestionBankItem,
  QuestionBankLessonRef,
  QuestionBankOption,
  QuestionBankSource,
  QuestionBankType,
} from './types.ts';
// .ts extension: these modules are in the node --test import graph, which resolves literally
import { QUESTION_BANK_LEVELS, QUESTION_BANK_TYPES } from './types.ts';

/**
 * A question in the board's own terms on its way to the server, and the rules it must pass.
 *
 * izteach-examination-service validates nothing on /question, so these checks are the only thing
 * standing between a teacher and an ungradable question in the school's bank. An import is checked
 * whole before the first POST, so a bad question never leaves the rest half-saved. izteach.ts
 * turns this into the IZ document.
 */
export interface QuestionBankPayload {
  content: string;
  type: QuestionBankType;
  level: number;
  options: QuestionBankOption[];
  explanation: string;
  source: QuestionBankSource;
  lessonRef: QuestionBankLessonRef | null;
}

/** A saved question or a draft on its way to becoming one - whatever toPayload needs to read. */
export type QuestionBankDraft = Pick<QuestionBankItem, 'content' | 'type' | 'level' | 'options' | 'source'> & {
  explanation?: string;
  lessonRef?: QuestionBankLessonRef;
};

// Sanity caps carried over from the old bank; the IZ side has none of its own.
const LIMITS = {
  content: 65536,
  explanation: 65536,
  optionContent: 8192,
  options: 26,
  lessonRefValue: 256,
};

const SOURCES: QuestionBankSource[] = ['ai-quiz-supporter', 'manual'];

function text(value: string | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Build the request body. Sanitisation already happened upstream; this only trims and shapes. */
export function toPayload(draft: QuestionBankDraft): QuestionBankPayload {
  const lesson = draft.lessonRef;
  const options = draft.options || [];
  return {
    content: text(draft.content),
    type: draft.type,
    level: draft.level,
    // an essay question has no answers to store
    options:
      draft.type === 'text'
        ? []
        : options.map((option) => {
          const shaped: QuestionBankOption = { content: text(option.content), isCorrect: option.isCorrect === true };
          if (option.key) shaped.key = option.key;
          return shaped;
        }),
    explanation: text(draft.explanation),
    source: draft.source,
    lessonRef: lesson
      ? { courseId: text(lesson.courseId), unitId: text(lesson.unitId), unitName: text(lesson.unitName) }
      : null,
  };
}

function optionsProblem(options: QuestionBankOption[], type: QuestionBankType): string | null {
  if (type === 'text') return null;
  if (options.length < 2) return 'cần ít nhất 2 đáp án';
  if (options.length > LIMITS.options) return `tối đa ${LIMITS.options} đáp án`;
  if (type === 'boolean' && options.length !== 2) return 'câu Đúng - Sai phải có đúng 2 đáp án';
  for (let i = 0; i < options.length; i += 1) {
    if (!options[i].content) return `đáp án ${i + 1} không được rỗng`;
    if (options[i].content.length > LIMITS.optionContent) {
      return `đáp án ${i + 1} vượt quá ${LIMITS.optionContent} ký tự`;
    }
  }
  const correct = options.filter((option) => option.isCorrect).length;
  if (correct === 0) return 'cần ít nhất 1 đáp án đúng';
  // a single-choice or Đúng - Sai question with two right answers can never be graded
  if (type !== 'multiple-choice' && correct !== 1) return 'loại câu hỏi này chỉ được có đúng 1 đáp án đúng';
  return null;
}

function lessonRefProblem(lessonRef: QuestionBankLessonRef | null): string | null {
  if (!lessonRef) return null;
  if (!lessonRef.courseId || !lessonRef.unitId) return 'bài giảng được gắn thiếu mã khóa học hoặc mã bài';
  const values = [lessonRef.courseId, lessonRef.unitId, lessonRef.unitName];
  for (let i = 0; i < values.length; i += 1) {
    if (values[i].length > LIMITS.lessonRefValue) return `thông tin bài giảng vượt quá ${LIMITS.lessonRefValue} ký tự`;
  }
  return null;
}

/** Why the server would refuse this question, in the teacher's words, or null if it would take it. */
export function payloadProblem(payload: QuestionBankPayload): string | null {
  if (!payload.content) return 'nội dung câu hỏi không được rỗng';
  if (payload.content.length > LIMITS.content) return `nội dung câu hỏi vượt quá ${LIMITS.content} ký tự`;
  if (QUESTION_BANK_TYPES.indexOf(payload.type) === -1) return 'loại câu hỏi không hợp lệ';
  if (QUESTION_BANK_LEVELS.indexOf(payload.level as 1 | 2 | 3) === -1) return 'độ khó phải là 1, 2 hoặc 3';
  if (SOURCES.indexOf(payload.source) === -1) return 'nguồn câu hỏi không hợp lệ';
  if (payload.explanation.length > LIMITS.explanation) return `giải thích vượt quá ${LIMITS.explanation} ký tự`;
  const options = optionsProblem(payload.options, payload.type);
  if (options) return options;
  return lessonRefProblem(payload.lessonRef);
}

/** The first question the server would refuse, numbered the way the teacher sees the list. */
export function batchProblem(payloads: QuestionBankPayload[]): string | null {
  for (let i = 0; i < payloads.length; i += 1) {
    const problem = payloadProblem(payloads[i]);
    if (problem) return `Câu hỏi ${i + 1}: ${problem}.`;
  }
  return null;
}

/** The bank stores no client id, so a question is recognised across devices by its content alone. */
export function dedupeKey(draft: QuestionBankDraft): string {
  return `${draft.type}|${draft.level}|${text(draft.content)}`;
}
